import {Accordion, AccordionDetails, AccordionSummary, Card, Grid} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {SwitchSegmentedYesNo, TextBox} from '../../../Core/UI';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {SelectInputElement} from '../Components/SelectInputElement';
import React from 'react';
import {observer} from 'mobx-react-lite';
import {HealthQuestionsProps} from './HealthQuestionsList';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/HealthXref';

const Diabetes = observer(({counter, valueChanged, parameters, coreQuestions}: HealthQuestionsProps) => {
    return (
        <Accordion style={{width: '100%'}} TransitionProps={{unmountOnExit: true}}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={'diabetes-content'} id={'diabetes-header'}>
                <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                    <div>Diabetes or high blood sugar</div>
                    <div>{counter('diabetes')}</div>
                </div>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid key="params" container>
                        {coreQuestions?.map(({name, label}) => (
                            <Grid item xs={4} key={name}>
                                <SwitchSegmentedYesNo
                                    param={{
                                        name: name,
                                        label: label,
                                        category: 'diabetes',
                                        ...(parameters[name as keyof typeof parameters] as Parameter),
                                    }}
                                    required={''}
                                    valueChanged={valueChanged}
                                />
                            </Grid>
                        ))}
                        {parameters.Type1Diabetes.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Type 1 Diabetes</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label={'When were you diagnosed?'}
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.type1DiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.type1DiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'type1A1CReading'}
                                                label={'What was your last A1C reading?'}
                                                value={parameters.type1A1CReading?.value}
                                                onChange={(value) => valueChanged(parameters.type1A1CReading, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'What treatments are you taking?',
                                                    name: 'type1Treatment',
                                                    options: fieldOptions.type1Treatment.options,
                                                    ...parameters.type1Treatment,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'type1Complications',
                                                    label: 'Any diabetic complications/Neuropathy?',
                                                    ...parameters.type1Complications,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Type2Diabetes.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Type 2 Diabetes</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label={'When were you diagnosed?'}
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.type2DiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.type2DiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'type2A1CReading'}
                                                label={'What was your last A1C reading?'}
                                                value={parameters.type2A1CReading?.value}
                                                onChange={(value) => valueChanged(parameters.type2A1CReading, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'What treatments are you taking?',
                                                    name: 'type2Treatment',
                                                    options: fieldOptions.type2Treatment.options,
                                                    ...parameters.type2Treatment,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SwitchSegmentedYesNo
                                                param={{
                                                    name: 'type2Complications',
                                                    label: 'Any diabetic complications/Neuropathy?',
                                                    ...parameters.type2Complications,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id={'type2DiagnosisAge'}
                                                label={'What age were you first diagnosed?'}
                                                value={parameters.type2DiagnosisAge?.value}
                                                onChange={(value) => valueChanged(parameters.type2DiagnosisAge, value)}
                                                type="number"
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                        {parameters.Hypoglycemia.value && (
                            <Grid item xs={12} style={{marginTop: 10}}>
                                <Card style={{padding: 20}}>
                                    <strong>Hypoglycemia</strong>
                                    <Grid container spacing={3}>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When were you diagnosed? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.hypoglycemiaDiagnosisDate?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.hypoglycemiaDiagnosisDate, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectInputElement
                                                param={{
                                                    label: 'Do you have a history of ?',
                                                    name: 'hypoglycemiaHistory',
                                                    options: fieldOptions.hypoglycemiaHistory.options,
                                                    ...parameters.hypoglycemiaHistory,
                                                }}
                                                required={''}
                                                valueChanged={valueChanged}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hypoglycemiaCondition"
                                                label="Describe your diabetic condition. "
                                                value={parameters.hypoglycemiaCondition?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.hypoglycemiaCondition, value)
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hypoglycemiaA1C"
                                                label="Have you had your Hemoglobin A1c (HbA1c) tested within the last 12 months and do you know the results? "
                                                value={parameters.hypoglycemiaA1C?.value}
                                                onChange={(value) => valueChanged(parameters.hypoglycemiaA1C, value)}
                                                type="number"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                                <DatePicker
                                                    inputVariant="outlined"
                                                    variant="inline"
                                                    openTo="year"
                                                    views={['year', 'month']}
                                                    label="When was your last episode of hypoglycemia? "
                                                    style={{width: '100%', marginTop: 8}}
                                                    value={parameters.hypoglycemiaLastEpisode?.value || null}
                                                    onChange={(date) =>
                                                        valueChanged(parameters.hypoglycemiaLastEpisode, date)
                                                    }
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextBox
                                                id="hypoglycemiaTreatment"
                                                label="Provide details on treatment received and date of diagnosis. "
                                                value={parameters.hypoglycemiaTreatment?.value}
                                                onChange={(value) =>
                                                    valueChanged(parameters.hypoglycemiaTreatment, value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
});

export default Diabetes;
